import { UseQueryResult } from "@tanstack/react-query"
import {
  ColumnDef,
  PaginationState,
  SortingState,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useEffect, useMemo, useState } from "react"

import { Table } from "~/components/table/Table"

type Props<T> = {
  columns: ColumnDef<T, any>[]
  countQuery: (
    filterOption: string | null,
    searchValue?: string
  ) => UseQueryResult<number, unknown>
  query: (
    fetchDataOptions: PaginationState,
    prismaSorting: {
      [x: string]: string
    }[],
    filterOption: string | null,
    searchValue?: string
  ) => UseQueryResult<T[], unknown>
  defaultPageSize: number
  defaultSorting: SortingState
  filterOption: string | null
  searchValue?: string
}

export function ControlledTable<T>({
  columns,
  countQuery,
  query,
  defaultPageSize,
  defaultSorting,
  filterOption,
  searchValue,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting)
  const [pageCount, setPageCount] = useState<number>(0)
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  })

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  )
  const defaultData = useMemo(() => [], [])
  const fetchDataOptions = {
    pageIndex,
    pageSize,
  }

  const prismaSorting =
    sorting.length > 0
      ? sorting.map((sort) => ({
          [sort.id]: sort.desc ? "desc" : "asc",
        }))
      : []

  const dataCount = countQuery(filterOption, searchValue)
  const dataQuery = query(
    fetchDataOptions,
    prismaSorting,
    filterOption,
    searchValue
  )

  useEffect(() => {
    if (dataCount && dataCount.data === 0) {
      setPageCount(0)
    }
    if (dataCount && dataCount.data) {
      setPageCount(Math.ceil(dataCount.data / pageSize))
    }
  }, [pageSize, dataCount])

  useEffect(() => {
    setPagination((pagination) => ({
      ...pagination,
      pageIndex: 0,
    }))
  }, [filterOption, searchValue])

  const table = useReactTable({
    data: dataQuery.data || defaultData,
    columns,
    state: {
      sorting,
      pagination,
    },
    manualPagination: true,
    pageCount,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,

    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <Table
      table={table}
      pagination={true}
      manualPaginationCount={dataCount.data || 0}
      pageIndex={pageIndex}
    />
  )
}
